/* eslint-disable import/no-cycle */
import {useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Col} from 'antd';
import Form from '../../shared/form/Form';
import Input from '../../shared/inputs/Input';
import QualityIndicator from '../qualityIndicator/QualityIndicator';
import {countDecimals} from '../../../utils/numbers';
import {ValidationRules} from '../../../utils/validationRules';
import styles from '../InspectionTaskPendingWidget.module.scss';
import InspectionTaskExecutionForm from './InspectionTaskExecutionForm';
import {InspectionTaskContext} from './InspectionTaskExecutionModal';
import {InspectionTaskValuation} from '../../../models/inspectionTaskResult';
import {useMount} from '../../../hooks/useMount';

const makeValueAccepted = (desired, low, high) => (value) => {
  if (Number(value) === Number(desired)) {
    return true;
  }
  if (low && high) {
    return value <= high && value >= low;
  }
  if (high || high === 0) {
    return value <= high;
  }
  if (low || low === 0) {
    return value >= low;
  }
  return false;
};

const Quantitative = ({inspectionTask, useUniformQualityIndicator, disabled, onSubmit}) => {
  const {t} = useTranslation();
  const {quantitative} = inspectionTask;
  const [form] = Form.useForm();
  const inspectionTaskContext = useContext(InspectionTaskContext);

  const formName = `quantitativeForm${inspectionTask.id}`;
  const results = inspectionTaskContext.rootForm.getFieldValue('results');
  const [submitDisabled, setSubmitDisabled] = useState(results[formName] === undefined);
  const [remarkRequired, setRemarkRequired] = useState(results[formName]?.valuation === 'R');
  const [value, setValue] = useState(results[formName]?.value || null);
  const [overruleActive, setOverruleActive] = useState(results[formName]?.overruledUserId || false);
  const [overruledUserId, setOverruleUserId] = useState(null);
  const inputRef = useRef();

  useMount(() => {
    inputRef.current.focus();
  });

  const valueAccepted = makeValueAccepted(
    quantitative.desiredValue,
    quantitative.toleranceLow,
    quantitative.toleranceHigh
  );

  const transform = (values) => ({
    valuation: valueAccepted(values.value) ? InspectionTaskValuation.accepted : InspectionTaskValuation.required,
    value: values.value,
    remark: values.remark || '',
    overruledValue: overruleActive ? values.overruledValue : undefined,
    overruledValuation: overruleActive ? InspectionTaskValuation.accepted : undefined,
    overruledRemark: values.overruledRemark || '',
    overruledUserId,
    inspectionTaskId: inspectionTask.id,
  });

  const decimalValidator = {
    validator: (rule, v) => {
      if (countDecimals(Number(v)) > quantitative.decimalPlaces) {
        return Promise.reject(t('errors.messages.maxDecimalPlaces', {decimalPlaces: quantitative.decimalPlaces}));
      }
      return Promise.resolve();
    },
  };

  const initialValues = {
    value: results[formName]?.value || null,
    remark: results[formName]?.remark || null,
    overruledValue: results[formName]?.overruledValue || null,
    overruledRemark: results[formName]?.overruledRemark || null,
  };

  const validationRules = [ValidationRules.number, ValidationRules.required, decimalValidator];
  if (quantitative.plausibleLow !== undefined && quantitative.plausibleLow !== null) {
    validationRules.push(ValidationRules.minNumber(quantitative.plausibleLow));
  }

  if (quantitative.plausibleHigh !== undefined && quantitative.plausibleHigh !== null) {
    validationRules.push(ValidationRules.maxNumber(quantitative.plausibleHigh));
  }

  return (
    <InspectionTaskExecutionForm
      name={formName}
      form={form}
      onValuesChange={(changedValues) => {
        if (changedValues.value) {
          setValue(Number(changedValues.value));
          const accepted = valueAccepted(changedValues.value);
          setRemarkRequired(!accepted);
          setSubmitDisabled(inspectionTask.mustBeAccepted && inspectionTask.isOverruleAllowed && !accepted);
        }
      }}
      initialValues={initialValues}
      onFinish={() => {
        const formResults = {...results};
        formResults[formName] = transform(form.getFieldsValue(true));
        inspectionTaskContext.rootForm.setFieldsValue({results: formResults});
        onSubmit(formResults[formName]);
      }}
      remarkRequired={remarkRequired && !overruleActive}
      overruledRemarkRequired={overruleActive}
      overruleActive={overruleActive}
      setOverruleActive={setOverruleActive}
      setOverruleUserId={(userId) => setOverruleUserId(userId)}
      inspectionTask={inspectionTask}
      submitDisabled={submitDisabled && !overruleActive}
    >
      <Col md={8} xs={24}>
        <Form.Item
          wrapperCol={{span: 24}}
          name={'value'}
          style={{marginBottom: 0}}
          rules={!overruleActive ? validationRules : [ValidationRules.number]}
        >
          <Input
            className={styles.quantitativeValueInput}
            quantitative={quantitative}
            addonAfter={quantitative.unit}
            disabled={disabled || overruleActive}
            autoComplete={'off'}
            ref={inputRef}
          />
        </Form.Item>
      </Col>
      {!inspectionTask.hideQualityIndicator && (
        <>
          <Col md={16} xs={24}>
            <div>{t('inspectionTaskPendingModal.taskBox.quality')}</div>
            <QualityIndicator
              values={[value]}
              useUniformQualityIndicator={useUniformQualityIndicator}
              {...quantitative}
            />
          </Col>
        </>
      )}
      {overruleActive && (
        <>
          <Col span={24}>
            {t('inspectionTaskPendingModal.taskBox.overruledValue')}
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              wrapperCol={{span: 24}}
              name={'overruledValue'}
              style={{marginBottom: 0}}
              rules={[ValidationRules.required, ValidationRules.number]}
            >
              <Input
                className={styles.quantitativeValueInput}
                quantitative={quantitative}
                addonAfter={quantitative.unit}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </>
      )}
    </InspectionTaskExecutionForm>
  );
};
export default Quantitative;
